import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { rawFetch } from "@/lib/api/client";
import { ErrorBox } from "@/components/common";

type Field = "term" | "definition" | "data_entity" | "synonyms" | "lob" | "product" |
  "business_unit" | "jurisdiction" | "language";
const FIELDS: { key: Field; label: string }[] = [
  { key: "term", label: "Term" }, { key: "definition", label: "Definition" },
  { key: "data_entity", label: "Data entity" }, { key: "synonyms", label: "Synonyms / aliases" },
  { key: "lob", label: "Line of business" }, { key: "product", label: "Product" },
  { key: "business_unit", label: "Business unit" }, { key: "jurisdiction", label: "Jurisdiction" },
  { key: "language", label: "Language" },
];
type Preview = {
  row_count: number; entity_count: number; unclassified_count: number;
  alias_count: number; rejected_count: number;
  rejected_rows: { row: number; reason: string }[];
  header_row: number; headers: string[]; column_map: Partial<Record<Field, number>>;
  preview: { row: number; term: string; data_entity: string; definition: string; aliases: string[]; review_flags: string[] }[];
};
type Staged = Preview & { proposal_count: number; created_count: number; run_id: string; review_run_id: string; proposal_ids: string[] };
export type ImportReceipt = { runId: string; proposalCount: number; proposalIds: string[] };
const TEMPLATE = "term,definition,data_entity,synonyms,lob,product,business_unit,jurisdiction,language\n" +
  "Covered Vehicle,A synthetic vehicle covered by a policy.,Vehicle,Insured Vehicle,motor,auto,claims,ES,en\n";

function downloadTemplate() {
  const url = URL.createObjectURL(new Blob([TEMPLATE], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url; link.download = "ontoligent-term-dictionary-template.csv";
  link.click(); URL.revokeObjectURL(url);
}

async function encode(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = "";
  for (let offset = 0; offset < bytes.length; offset += 32_768) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + 32_768));
  }
  return btoa(binary);
}

export function DictionaryImporter({ workspaceId, onStaged }: { workspaceId: string; onStaged?: (receipt: ImportReceipt) => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [encoded, setEncoded] = useState("");
  const [preview, setPreview] = useState<Preview | null>(null);
  const [mapping, setMapping] = useState<Partial<Record<Field, number>>>({});
  const [mappedPreview, setMappedPreview] = useState("");
  const [staged, setStaged] = useState<Staged | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  async function inspect(currentMapping?: Partial<Record<Field, number>>, currentHeader?: number) {
    if (!file || !workspaceId) return;
    setBusy(true); setError(null); setStaged(null);
    try {
      if (!/\.(csv|xlsx)$/i.test(file.name) || file.size > 5_000_000) {
        throw new Error("Choose CSV or macro-free XLSX up to 5 MB and 10,000 rows.");
      }
      const data = encoded || await encode(file);
      setEncoded(data);
      const result = await rawFetch<Preview>("/ontology/term-sets/import/", {
        method: "POST", body: JSON.stringify({ workspace: workspaceId, file_name: file.name,
          content_base64: data, preview: true,
          ...(currentMapping ? { column_map: currentMapping } : {}),
          ...(currentHeader || currentMapping ? { header_row: currentHeader ?? preview?.header_row } : {}),
        }),
      });
      setPreview(result);
      setMapping(result.column_map);
      setMappedPreview(JSON.stringify(result.column_map));
    } catch (caught) { setError(caught as Error); }
    finally { setBusy(false); }
  }

  async function stage() {
    if (!file || !preview || !encoded || !workspaceId) return;
    setBusy(true); setError(null);
    try {
      const result = await rawFetch<Staged>("/ontology/term-sets/import/", {
        method: "POST", body: JSON.stringify({ workspace: workspaceId, file_name: file.name,
          content_base64: encoded, column_map: mapping, header_row: preview.header_row,
        }),
      });
      setStaged(result);
      onStaged?.({ runId: result.review_run_id || result.run_id,
        proposalCount: result.proposal_count, proposalIds: result.proposal_ids });
    } catch (caught) { setError(caught as Error); }
    finally { setBusy(false); }
  }

  const mappingCurrent = mappedPreview === JSON.stringify(mapping);
  return <section className="card space-y-3" aria-label="Import dictionary">
    <div>
      <h2 className="font-semibold">Import dictionary for review</h2>
      <p className="text-xs text-muted">CSV or macro-free XLSX, up to 5 MB and 10,000 rows. Parsed in memory; formulas and external links are never run. Structured identifiers are redacted; possible names and addresses are flagged for review. No original file or searchable chunks are stored.</p>
    </div>
    <div className="flex flex-wrap items-center gap-2">
      <input aria-label="Dictionary file" type="file" accept=".csv,.xlsx" className="block min-w-0 text-xs"
        onChange={(event) => {
          setFile(event.target.files?.[0] ?? null); setEncoded(""); setPreview(null);
          setMapping({}); setStaged(null); setError(null);
        }} />
      <button className="btn" onClick={downloadTemplate}>Download template</button>
    </div>
    <button className="btn" disabled={!file || busy} onClick={() => inspect()}>
      {busy ? "Reading…" : "Preview file"}
    </button>
    {preview && <>
      <p role="status" className="text-xs font-medium">{preview.row_count.toLocaleString()} terms found, {preview.entity_count} data entities, {preview.unclassified_count} unclassified, {preview.alias_count} aliases, {preview.rejected_count} rows rejected</p>
      <label className="block text-[11px] text-muted">Header row
        <select className="input ml-2 w-auto" value={preview.header_row}
          onChange={(event) => void inspect(undefined, Number(event.target.value))}>
          {Array.from({ length: 10 }, (_, index) => <option key={index} value={index + 1}>{index + 1}</option>)}
        </select>
        <span className="ml-2">Map columns below, then refresh the preview before staging.</span>
      </label>
      <div className="grid gap-2 sm:grid-cols-3">
        {FIELDS.map(({ key, label }) => <label key={key} className="text-xs">{label}{["term", "definition"].includes(key) ? " *" : ""}
          <select className="input mt-1" value={mapping[key] ?? ""} onChange={(event) => {
            const value = event.target.value;
            setMapping((old) => { const next = { ...old }; if (value) next[key] = Number(value); else delete next[key]; return next; });
          }}>
            <option value="">Not mapped</option>
            {preview.headers.map((header, index) => <option key={index} value={index}>{index + 1}: {header || "(blank)"}</option>)}
          </select>
        </label>)}
      </div>
      {!mappingCurrent && <button className="btn" disabled={busy} onClick={() => inspect(mapping)}>Refresh preview</button>}
      {preview.preview.length > 0 && <div className="max-h-52 overflow-auto border border-border text-xs">
        <table className="w-full"><thead><tr><th className="text-left">Row</th><th className="text-left">Term</th><th className="text-left">Data entity</th><th className="text-left">Definition</th></tr></thead>
          <tbody>{preview.preview.map((row) => <tr key={row.row} className="border-t border-border">
            <td>{row.row}</td><td>{row.term}</td><td>{row.data_entity || "Unclassified"}</td><td>{row.definition}{row.review_flags?.length > 0 && <span className="block text-amber-600">Review possible name or address</span>}</td>
          </tr>)}</tbody></table>
      </div>}
      {preview.rejected_rows.length > 0 && <details className="text-xs"><summary>Unreadable rows ({preview.rejected_count})</summary>
        <ul>{preview.rejected_rows.map((row) => <li key={row.row}>Row {row.row}: {row.reason}</li>)}</ul>
      </details>}
      <button className="btn-primary" disabled={busy || !mappingCurrent || !preview.row_count ||
        mapping.term === undefined || mapping.definition === undefined} onClick={stage}>Stage {preview.row_count} proposals for review</button>
    </>}
    {staged && <p role="status" className="text-xs text-good">{staged.row_count} terms staged; {staged.created_count} new proposals. <Link to="/catalog" className="underline">Review proposals in Catalog</Link>.</p>}
    {error && <ErrorBox error={error} />}
  </section>;
}
